import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { fetchSeasonStandings } from '../apiClient/seasons';
import {
  SEASON_SCOPES,
  type CurrentSeason,
  type SeasonMapCrown,
  type SeasonScope,
  type SeasonStanding,
} from '@shared/seasons';
import type { GameMode } from '@shared/chart';
import { useAuth } from './AuthProvider';

interface SeasonContextValue {
  /** Active season, or `null` until the first fetch resolves. */
  season: CurrentSeason | null;
  /** The player's standing in every scope (overall + each mode). */
  standings: SeasonStanding[];
  /** The player's standing in one scope (zeros + null rank if unfetched/unranked). */
  standingFor: (scope: SeasonScope) => SeasonStanding;
  /**
   * Live crowns the player holds on a single `(mode, mapSlug)` map (0 if they
   * aren't top 3 on it). Drives the per-song crown on the home-page trophy
   * strips. The mode is required — Pulse and Circles share slugs.
   */
  crownsForMap: (mapSlug: string, mode: GameMode) => number;
  /** Refetch from the server. Called on mount and after each run's results. */
  refresh: () => Promise<void>;
}

/** A zeroed standing for a scope — used before data loads and when unranked. */
function emptyStanding(scope: SeasonScope): SeasonStanding {
  return { scope, rank: null, participants: 0, stars: 0, cups: 0, crowns: 0, total: 0 };
}

const EMPTY_STANDINGS: SeasonStanding[] = SEASON_SCOPES.map(emptyStanding);

const SeasonContext = createContext<SeasonContextValue | null>(null);

export function useSeason(): SeasonContextValue {
  const ctx = useContext(SeasonContext);
  if (!ctx) {
    throw new Error('useSeason must be used inside <SeasonProvider>');
  }
  return ctx;
}

/**
 * Holds the player's live season standings (the three currency totals + rank
 * per scope). Unlike {@link ProgressionProvider}, there's no optimistic
 * update: crowns shift with *other* players' runs too, so the only honest
 * source is a server refetch — triggered on mount and after each run's
 * results overlay appears.
 */
export function SeasonProvider({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const [season, setSeason] = useState<CurrentSeason | null>(null);
  const [standings, setStandings] = useState<SeasonStanding[]>(EMPTY_STANDINGS);
  const [mapCrowns, setMapCrowns] = useState<SeasonMapCrown[]>([]);
  // Bumped on every auth change. A refresh captures the epoch at call time and
  // drops its result if the epoch has since moved — so a `/me` still in flight
  // when the player logs out or switches accounts can't overwrite the cleared
  // (or next user's) standings with the previous user's crowns.
  const epochRef = useRef(0);

  const refresh = useCallback(async () => {
    const epoch = epochRef.current;
    try {
      const next = await fetchSeasonStandings();
      if (epoch !== epochRef.current) {
        return;
      }
      setSeason(next.season);
      setStandings(next.standings);
      setMapCrowns(next.mapCrowns);
    } catch (err) {
      console.warn('Failed to fetch season standings:', err);
    }
  }, []);

  useEffect(() => {
    epochRef.current += 1;
    if (status !== 'ready' || !user) {
      setSeason(null);
      setStandings(EMPTY_STANDINGS);
      setMapCrowns([]);
      return;
    }
    void refresh();
  }, [status, user, refresh]);

  const standingFor = useCallback(
    (scope: SeasonScope): SeasonStanding =>
      standings.find((s) => s.scope === scope) ?? emptyStanding(scope),
    [standings],
  );

  const crownsForMap = useCallback(
    (mapSlug: string, mode: GameMode): number => {
      const found = mapCrowns.find((c) => c.mode === mode && c.mapSlug === mapSlug);
      return found ? found.crowns : 0;
    },
    [mapCrowns],
  );

  const value = useMemo<SeasonContextValue>(
    () => ({ season, standings, standingFor, crownsForMap, refresh }),
    [season, standings, standingFor, crownsForMap, refresh],
  );

  return <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>;
}
